import Box from "@mui/material/Box"
import Drawer from "@mui/material/Drawer"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { ReactNode, useEffect, useState } from "react"
import translations from "src/locales/en/default.json"
import { playerStore } from "src/store/PlayerStore"

import { useModalDismiss } from "../../../../hooks/use-modal-dismiss"
import useTranslate, { Namespaces } from "../../../../hooks/use-translate"
import { TranslationKey } from "../../../../types"
import { s } from "../../../../utilities/common"
import { theme, TI } from "../../../../utilities/theme"
import { DGText } from "../../../common/DGText"
import { PlayerUIFocusKeys } from "../../player-ui-focus-keys"
import { usePlayerTracking } from "../../use-player-tracking"

interface RadioIconProps {
  selected?: boolean
  disabled?: boolean
}
const RadioIcon = ({ selected = false, disabled = false }: RadioIconProps) => {
  return selected ? (
    <svg
      width={s("40")}
      height={s("40")}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="20"
        cy="20"
        r="18.5"
        stroke="white"
        strokeOpacity={disabled ? 0.3 : 1}
        strokeWidth="3"
      />
      <circle cx="20" cy="20" r="8" fill="white" />
    </svg>
  ) : (
    <svg
      width={s("40")}
      height={s("40")}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="20"
        cy="20"
        r="18.5"
        stroke="white"
        strokeOpacity={disabled ? 0.3 : 0.55}
        strokeWidth="3"
      />
    </svg>
  )
}

interface CheckItemProps {
  selected?: boolean
  disabled?: boolean
  value: false | number | string
  onPress: () => void
  children: ReactNode
  focusPrefix: string
  ariaLabel: string
}

export const CheckItem = ({
  selected = false,
  disabled = false,
  value,
  onPress,
  children,
  focusPrefix,
  ariaLabel,
}: CheckItemProps) => {
  const { ref, focused, focusSelf } = useFocusable({
    focusable: !disabled,
    onEnterPress: onPress,
    focusKey: focusPrefix + (value === false ? "false" : value),
  })

  return (
    <Box
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={onPress}
      ref={ref}
      sx={{
        mb: "1px", // focus breaks if there is no spacing between items sometimes (especially on lower res screens)
        width: "100%",
        backgroundColor: focused ? theme.palette.secondary.variant : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          pt: s("24px"),
          pb: s("24px"),
          pl: s("56px"),
          height: s("40px"),
        }}
      >
        <RadioIcon selected={selected} disabled={disabled} />
        <Box sx={{ ml: s("32px") }}>{children}</Box>
      </Box>
    </Box>
  )
}

export const SubSelect = () => {
  const subtitles = playerStore((state) => state.subtitles)
  const hls = playerStore((state) => state.hls)
  const setSubtitleSelectOpen = playerStore(
    (state) => state.setSubtitleSelectOpen,
  )
  const subtitleSelectOpen = playerStore((state) => state.subtitleSelectOpen)
  const currentSubTrack = playerStore((state) => state.currentSubTrack)

  const track = usePlayerTracking("subtitle_opened")
  const trackSelect = usePlayerTracking("subtitle_language_selected")

  const { focusKey, ref } = useFocusable({
    isFocusBoundary: true,
  })

  const [sub, setSub] = useState<false | number>(false)
  const t = useTranslate()

  useEffect(() => {
    if (hls) {
      if (hls.subtitleDisplay === false) setSub(false)
      else setSub(hls.subtitleTrack)
    }
  }, [subtitleSelectOpen])

  useModalDismiss(() => {
    setFocus(PlayerUIFocusKeys.SubButton)
    setSubtitleSelectOpen(false)
  })

  useEffect(() => {
    if (subtitleSelectOpen) {
      track({
        subtitle_language_available: hls?.subtitleTracks
          .map((e) => e.name)
          .join("/"),
      })
      setFocus(
        PlayerUIFocusKeys.SubtitlePrefix + (sub === false ? "false" : sub),
      )
    }
  }, [subtitleSelectOpen, sub])

  if (!subtitles || !hls) return null
  return (
    <div>
      <>
        <Drawer
          anchor={"right"}
          open={subtitleSelectOpen}
          onClose={() => setSubtitleSelectOpen(false)}
        >
          <Box
            sx={{
              width: s("794px"),
              backgroundColor: theme.palette.primary.main,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              pt: s("120px"),
            }}
            role="presentation"
            onClick={() => {}}
          >
            <DGText
              tx={TI.H05tv}
              sx={{
                color: theme.palette.white.main,
                pb: s("66px"),
                pl: s("56px"),
              }}
            >
              {t("player__settings_subtitles")}
            </DGText>
            <FocusContext.Provider value={focusKey}>
              <Box ref={ref}>
                <CheckItem
                  ariaLabel={t("player__settings_subtitles_none")}
                  onPress={() => {
                    trackSelect({
                      subtitle_language_available: hls?.subtitleTracks
                        .map((e) => e.name)
                        .join("/"),
                      subtitle_language: "none",
                    })
                    hls.subtitleDisplay = false
                    currentSubTrack.set(false)
                    setSub(false)
                    setSubtitleSelectOpen(false)
                    setFocus(PlayerUIFocusKeys.SubButton)
                  }}
                  selected={sub === false}
                  value={false}
                  focusPrefix={PlayerUIFocusKeys.SubtitlePrefix}
                >
                  <DGText
                    tx={TI.Subheadline05tv}
                    sx={{
                      color: theme.palette.white.main,
                    }}
                  >
                    {t("player__settings_subtitles_none")}
                  </DGText>
                </CheckItem>
                {subtitles.map((e, i) => {
                  const title =
                    `subtitle_language_${e.toLowerCase()}` in translations
                      ? t(
                          `subtitle_language_${e.toLowerCase()}` as TranslationKey<Namespaces.Common>,
                        )
                      : e
                  return (
                    <CheckItem
                      key={"subtitle_" + e}
                      ariaLabel={title}
                      onPress={() => {
                        trackSelect({
                          subtitle_language_available: hls?.subtitleTracks
                            .map((e) => e.name)
                            .join("/"),
                          subtitle_language: hls.subtitleTracks[i].name,
                        })
                        hls.subtitleDisplay = true
                        hls.subtitleTrack = i
                        currentSubTrack.set(hls.subtitleTracks[i].name)
                        setSub(i)
                        setSubtitleSelectOpen(false)
                        setFocus(PlayerUIFocusKeys.SubButton)
                      }}
                      selected={sub === i}
                      value={i}
                      focusPrefix={PlayerUIFocusKeys.SubtitlePrefix}
                    >
                      <DGText
                        tx={TI.Subheadline05tv}
                        sx={{
                          color: theme.palette.white.main,
                        }}
                      >
                        {title}
                      </DGText>
                    </CheckItem>
                  )
                })}
              </Box>
            </FocusContext.Provider>
          </Box>
        </Drawer>
      </>
    </div>
  )
}
